import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

import {
  useActiveContributors,
  useActiveRepos,
  useCommitHistory,
  useContributorStats,
  useDashboardStats,
  useMonthlyCommits,
} from "../../lib/githubHooks";
import { Alert, Typography, useMediaQuery } from "@mui/material";
import { formatDistance } from "date-fns";

import { Tooltip } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import StatsCard from "../../components/github/StatsCard";
import LoadingIndicator from "../../components/github/loader";
import CardComponent from "../../components/github/CardComponent";
import ChartCard from "../../components/github/ChartCard";
import Footer from "../../components/common/Footer";
import Header from "../../components/common/Header";
import Navbar from "../../components/common/NavBar";

const processChartData = (
  chartData: Record<string, number>,
  labelName: string
) => {
  const currentMonth = new Date().getMonth();
  const dateLabels = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const monthLabels = Array.from({ length: 12 }, (_, i) => {
    const monthIndex = (currentMonth + i + 1) % 12;
    return dateLabels[monthIndex];
  });

  const months = Array.from({ length: 12 }, (_, i) => {
    const monthIndex = ((currentMonth + i + 1) % 12) + 1;
    return chartData[monthIndex.toString()] || 0;
  });

  return {
    labels: monthLabels,
    datasets: [
      {
        label: labelName,
        data: months,
        fill: false,
        borderColor: "rgb(159, 204, 46)",
        backgroundColor: "rgb(159, 204, 46)",
        barPercentage: 0.4,
        borderRadius: 10,
      },
    ],
  };
};

const Dashboard = () => {
  const { data, loading } = useDashboardStats();

  const { contributorsData, contributorsLoading, contributorsError } =
    useContributorStats();

  const { chartData, chartLoading, error: chartError } = useMonthlyCommits();

  const processedChartDataBar = processChartData(
    chartData?.monthly_commits ?? {},
    "Commits"
  );

  const { commits, commitsLoading, error: commitsError } = useCommitHistory();
  const {
    activeRepoData,
    activeRepoLoading,
    error: activeRepoError,
  } = useActiveRepos();

  const processedChartDataLine = processChartData(
    activeRepoData?.monthly_active_repos ?? {},
    "Active Repositories"
  );

  const {
    ActiveContributorsData,
    ActiveContributorsLoading,
    error: activeContributorsError,
  } = useActiveContributors();

  const isBelow1200 = useMediaQuery("(max-width:1200px)");

  const isBelow1600 = useMediaQuery("(max-width:1600px)");

  const isBelow800 = useMediaQuery("(max-width:800px)");

  const date = new Date();

  const now_utc = date.toISOString().slice(0, -5);

  const infoDetails = {
    commitsChartDescription: {
      desc: "Number of monthly commits in the last 12 months. Updated weekly",
      time:
        "Last updated " +
        formatDistance(chartData?.last_updated ?? 0, now_utc, {
          addSuffix: true,
        }).toString(),
    },
    activeReposChartDescription: {
      desc: "A repository is considered active if it has at least 1 commit in that particular month. Updated Weekly",
      time:
        "Last updated " +
        formatDistance(activeRepoData?.last_updated ?? 0, now_utc, {
          addSuffix: true,
        }).toString(),
    },
    activeContributorsDescription: {
      desc: "Number of active contributors in the last 30 days. Updated hourly",
      time:
        "Last updated " +
        formatDistance(ActiveContributorsData?.last_updated ?? 0, now_utc, {
          addSuffix: true,
        }).toString(),
    },
    recentCommitsDescription: {
      desc: "Top 500 most recent commits.",
      time:
        "Last updated " +
        formatDistance(commits?.last_updated ?? 0, now_utc, {
          addSuffix: true,
        }).toString(),
    },
    topContributorsDescription: {
      desc: "Top 10 contributors of the last 30 days ranked by commits.",
      time:
        "Last updated " +
        formatDistance(contributorsData?.last_updated ?? 0, now_utc, {
          addSuffix: true,
        }).toString(),
    },
    statDescription: {
      desc: "",
      time:
        "Last updated " +
        formatDistance(data?.last_updated ?? 0, now_utc, {
          addSuffix: true,
        }).toString(),
    },
  };

  return (
    <div>
      <Navbar />
      <Header pageName={"Cardano Github Statistics"} />

      <Box sx={{ width: "100%", maxWidth: "1920px" }}>
        <Box
          sx={{
            width: "90%",
            marginLeft: "5%",
            mt: 5,
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="flex-end">
            <Tooltip
              title={
                <>
                  <Typography fontSize={16}>
                    {infoDetails.statDescription.time}
                  </Typography>
                </>
              }
            >
              <InfoOutlined
                color="action"
                fontSize="small"
                sx={{ cursor: "pointer" }}
              />
            </Tooltip>
          </Box>
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sm={isBelow800 ? 12 : 6}
              md={isBelow1600 ? 6 : 3}
            >
              <StatsCard
                title="Total Repositories"
                value={loading ? "..." : data?.total_repos ?? "N/A"}
                change={loading ? "" : `${data?.new_repos_last_week}`}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={isBelow800 ? 12 : 6}
              md={isBelow1600 ? 6 : 3}
            >
              <StatsCard
                title="Total Contributors"
                value={loading ? "..." : data?.total_contributors ?? "N/A"}
                change={loading ? "" : `${data?.new_contributors_last_week}`}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={isBelow800 ? 12 : 6}
              md={isBelow1600 ? 6 : 3}
            >
              <StatsCard
                title="Total PRs"
                value={loading ? "..." : data?.total_prs ?? "N/A"}
                change={loading ? "" : `${data?.new_prs_last_week}`}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={isBelow800 ? 12 : 6}
              md={isBelow1600 ? 6 : 3}
            >
              <StatsCard
                title="Total Commits"
                value={loading ? "..." : data?.total_commits ?? "N/A"}
                change={loading ? "" : `${data?.new_commits_last_week}`}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mx: "auto", width: "90%" }}>
          <Grid container spacing={10} mt={1}>
            <Grid item xs={12} sm={12} md={isBelow1600 ? 12 : 4}>
              {contributorsLoading ? (
                <LoadingIndicator />
              ) : (
                <>
                  {contributorsError ? (
                    <div className="alertContainer">
                      <Alert severity="error">
                        {JSON.stringify(contributorsError)}
                      </Alert>
                    </div>
                  ) : (
                    <CardComponent
                      title="Top Contributors"
                      data={contributorsData?.top_contributors || {}}
                      type="List"
                      isBelow800={isBelow800}
                      info={infoDetails.topContributorsDescription}
                    />
                  )}
                </>
              )}
            </Grid>
            <Grid item xs={12} sm={12} md={isBelow1600 ? 12 : 8}>
              {activeRepoLoading ? (
                <LoadingIndicator />
              ) : (
                <>
                  {activeRepoError ? (
                    <div className="alertContainer">
                      <Alert severity="error">
                        {JSON.stringify(activeRepoError)}
                      </Alert>
                    </div>
                  ) : (
                    <ChartCard
                      title="Active Repositories"
                      data={processedChartDataLine}
                      type="Line"
                      isBelow800={isBelow800}
                      info={infoDetails.activeReposChartDescription}
                    />
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mx: "auto", width: "90%" }}>
          <Grid container spacing={10} mt={1}>
            <Grid item xs={12} sm={12} md={isBelow1600 ? 12 : 4}>
              {ActiveContributorsLoading ? (
                <LoadingIndicator />
              ) : (
                <>
                  {" "}
                  {activeContributorsError ? (
                    <div className="alertContainer">
                      <Alert severity="error">
                        {JSON.stringify(activeContributorsError)}
                      </Alert>
                    </div>
                  ) : (
                    <CardComponent
                      title="Contributors"
                      data={ActiveContributorsData?.active_contributors || {}}
                      type="Ring"
                      isBelow800={isBelow800}
                      info={infoDetails.activeContributorsDescription}
                    />
                  )}
                </>
              )}
            </Grid>
            <Grid item xs={12} sm={12} md={isBelow1600 ? 12 : 8}>
              {chartLoading ? (
                <LoadingIndicator />
              ) : (
                <>
                  {chartError ? (
                    <div className="alertContainer">
                      <Alert severity="error">
                        {JSON.stringify(chartError)}
                      </Alert>
                    </div>
                  ) : (
                    <ChartCard
                      title="Commits"
                      data={processedChartDataBar}
                      type="Bar"
                      isBelow800={isBelow800}
                      info={infoDetails.commitsChartDescription}
                    />
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mx: "auto", width: "90%" }}>
          <Grid container spacing={10} mt={1} mb={5}>
            <Grid item xs={12}>
              {commitsLoading ? (
                <LoadingIndicator />
              ) : (
                <>
                  {commitsError ? (
                    <div className="alertContainer">
                      <Alert severity="error">
                        {JSON.stringify(commitsError)}
                      </Alert>
                    </div>
                  ) : (
                    <CardComponent
                      title="Recent Commits"
                      data={commits?.recent_commits || {}}
                      type="ListCommits"
                      isBelow800={isBelow800}
                      info={infoDetails.recentCommitsDescription}
                    />
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Box>
        <div style={{ width: "100%", height: "20px" }}></div>
      </Box>

      <Footer />
    </div>
  );
};

export default Dashboard;
